import React, { useEffect } from 'react';

import SEO from '../SEO';import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, ArrowRight, Droplets, AlertTriangle, Clock, DollarSign, Wrench, Shield } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const LeaksSeepage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const leakTypes = [
    {
      title: 'Pipe & Fitting Leaks',
      description: 'Leaks at joints, threads, flanges, and along pipe bodies due to corrosion, vibration, or thermal cycling.',
      applications: ['Process piping', 'Steam lines', 'Compressed air systems', 'Hydraulic lines'],
      solution: 'Pipe repair composites and leak sealing compounds'
    },
    {
      title: 'Tank & Vessel Leaks',
      description: 'Seepage through tank walls, bottoms, and seams caused by corrosion, erosion, or mechanical damage.',
      applications: ['Storage tanks', 'Process vessels', 'Secondary containment', 'Water tanks'],
      solution: 'Tank lining systems and structural repair composites'
    },
    {
      title: 'Valve & Pump Leaks',
      description: 'Leaks from valve bodies, bonnets, packings, and pump casings affecting system efficiency.',
      applications: ['Gate valves', 'Ball valves', 'Pump housings', 'Mechanical seals'],
      solution: 'Metal repair pastes and rebuild composites'
    },
    {
      title: 'Structural Seepage',
      description: 'Water infiltration through concrete, masonry, and building envelope components.',
      applications: ['Foundations', 'Retaining walls', 'Roof systems', 'Expansion joints'],
      solution: 'Waterproof membranes and injection systems'
    }
  ];

  const consequences = [
    { icon: DollarSign, title: 'Product Loss', description: 'Valuable materials escaping the system' },
    { icon: AlertTriangle, title: 'Safety Hazards', description: 'Slip risks and chemical exposure' },
    { icon: Clock, title: 'Downtime', description: 'Unplanned shutdowns for emergency repairs' },
    { icon: Shield, title: 'Environmental Issues', description: 'Regulatory violations and cleanup costs' }
  ];

  const repairAdvantages = [
    'Repairs made without system shutdown',
    'Works on active leaks underwater',
    'No hot work permits required',
    'Permanent, long-lasting solutions',
    'Chemical and pressure resistant',
    'Rapid cure times available'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title="Leak & Seepage Solutions" description="Industrial leak and seepage repair solutions using epoxy systems and composite wraps." path="/research/leaks-seepage" />
      
      <PageHero
        badge="Research & Learn"
        title="Leaks & Seepage"
        description="From minor drips to major failures, leaks waste resources, create hazards, and signal bigger problems. Learn how to identify, prevent, and repair leaks effectively."
        backgroundKey="leaks-seepage"
        buttonText="Get Leak Assessment"
      />

      {/* Leak Types */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Common Leak Scenarios</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {leakTypes.map((type, index) => (
              <Card key={index} className="border-t-4 border-t-blue-600">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Droplets className="text-blue-600 mr-3" size={24} />
                    {type.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{type.description}</p>
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-slate-700 mb-2">Common Applications:</p>
                    <div className="flex flex-wrap gap-2">
                      {type.applications.map((app, idx) => (
                        <Badge key={idx} variant="outline" className="text-gray-600">{app}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="bg-green-50 p-3 rounded">
                    <p className="text-sm"><span className="font-semibold text-green-700">Our Solution:</span> {type.solution}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Consequences */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">The True Cost of Leaks</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {consequences.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm text-center">
                  <IconComponent className="mx-auto text-red-600 mb-4" size={40} />
                  <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Repair Advantages */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="bg-blue-700 text-white p-8 rounded-lg">
              <Wrench className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Cold-Applied Leak Repair</h3>
              <p className="text-blue-100 mb-6">
                Our advanced epoxy and polymer systems can seal leaks permanently without welding, 
                hot work, or system shutdown. Many repairs can be made on active leaks.
              </p>
              <Button className="bg-white text-blue-700 hover:bg-gray-100" onClick={() => navigate('/services/pipeline-repair')}>
                View Pipe Repair Services <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose Our Leak Solutions?</h2>
              <div className="space-y-3">
                {repairAdvantages.map((advantage, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="text-green-700 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">{advantage}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Response */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-red-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Clock className="mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold mb-4">Emergency Leak Response</h2>
          <p className="text-red-100 mb-8">
            Active leak causing problems? Our emergency response team is available 24/7 
            to provide rapid leak sealing solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-red-700 hover:bg-gray-100">
              <a href="tel:9493719948" className="flex items-center">
                Call (949) 371-9948
              </a>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-red-800" onClick={() => navigate('/#contact')}>
              Request Emergency Service
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LeaksSeepage;
